// src/services/apiService.js
import { authService } from './authService';

class ApiService {
  constructor() {
    this.api = authService.api;
  }

  // ===================Admin endpoints===================
  async getUsers(page = 1, per_page = 20, filters = {}) {
    const response = await this.api.get('/admin/users', { params: { page, per_page, ...filters } });
    return response.data;
  }

  async updateUserRole(userId, role) {
    const response = await this.api.put(`/admin/user/${userId}/role`, { role });
    return response.data;
  }

  async blockUser(userId, block = true) {
    const response = await this.api.put(`/admin/user/${userId}/block`, { block });
    return response.data;
  }

  async getJobDescriptions(page = 1, per_page = 20) {
    const response = await this.api.get('/admin/job-descriptions', { params: { page, per_page } });
    return response.data;
  }

  async deleteJobDescription(jobDescriptionId) {
    const response = await this.api.delete(`/admin/job-descriptions/${jobDescriptionId}`);
    return response.data;
  }

  async createJobDescription(data) {
    // Check if data is FormData (file upload) or regular object
    if (data instanceof FormData) {
      // File upload
      const response = await this.api.post('/admin/job-descriptions', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } else {
      // Regular JSON data
      const response = await this.api.post('/admin/job-descriptions', data);
      return response.data;
    }
  }
  async uploadResumes(formData) {
    const response = await this.api.post('/admin/resumes/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  async getResumes(jobId, page = 1, per_page = 20, status = null) {
    const params = { job_description_id: jobId, page, per_page };
    if (status) params.status = status;
    const response = await this.api.get('/admin/resumes', { params });
    console.log("Resumes Response:", response);
    return response.data;
  }

  async rateResumes(data) {
    const response = await this.api.post('/admin/resumes/rate-batch', data);
    return response.data;
  }

  async updateResumeStatus(resumeId, status) {
    const response = await this.api.put(`/admin/resume/${resumeId}/status`, { status });
    return response.data;
  }

  async deleteResume(resumeId) {
    const response = await this.api.delete(`/resume/${resumeId}`);
    return response.data;
  }

async generateAnswerFeedback(answerId, aiProvider = 'firework', aiModel = 'deepseek-v3') {
  const response = await this.api.post(`/admin/answer/${answerId}/generate-feedback`, {
    ai_provider: aiProvider,
    ai_model: aiModel
  });
  console.log("Generate Answer Feedback Response:", response);
  return response.data;
}

async generateAllFeedback(interviewId, aiProvider = 'firework', aiModel = 'deepseek-v3') {
  const response = await this.api.post(`/admin/interview/${interviewId}/generate-all-feedback`, {
    ai_provider: aiProvider,
    ai_model: aiModel
  });
  return response.data;
}
async createInterview(data) {
    const payload = {
      title: data.title,
      job_description_id: data.job_description_id,
      description: data.description,
      duration_minutes:  Number(data.duration_minutes),
      custom_questions_only: data.custom_questions_only,
      ai_generated_questions: Number(data.ai_generated_questions) || 0,
    };

    // Add custom questions if provided
    if (data.custom_questions && data.custom_questions.length > 0) {
      payload.custom_questions = data.custom_questions;
    }

    const response = await this.api.post('/admin/interviews', payload);
    return response.data;
  }

  async getInterviews(page = 1, per_page = 20) {
    const response = await this.api.get('/admin/interviews', { params: { page, per_page } });
    return response.data;
  }

  // Updated scheduling to support multiple candidates
  async scheduleInterview(interviewId, candidateIds) {
    const response = await this.api.post(`/admin/interview/${interviewId}/schedule`, {
      candidate_ids: candidateIds,
      notify: true
    });
    return response.data;
  }

  async getInterviewResults(interviewId) {
    const response = await this.api.get(`/admin/interview/${interviewId}/results`);
    return response.data;
  }

  // New method to get interview details with questions
  async getInterviewDetails(interviewId) {
    const response = await this.api.get(`/interview/${interviewId}`);
    return response.data;
  }

  // New method to get interview questions
  async getInterviewQuestions(interviewId) {
    const response = await this.api.get(`/interview/${interviewId}/questions`);
    return response.data;
  }

  // New method to get candidate answers for an interview
  async getCandidateAnswers(interviewId, candidateId) {
    const response = await this.api.get(`/interview/${interviewId}/candidate/${candidateId}/answers`);
    return response.data;
  }

  async getDashboardAnalytics() {
    const response = await this.api.get('/admin/analytics/dashboard');
    return response.data;
  }

// Add these methods to your existing apiService.js

// Enhanced resume rating with custom weightage - ASYNC VERSION
async rateResumesWithWeightage(data) {
  const response = await this.api.post('/admin/resumes/rate-batch-enhanced', data);
  console.log("Rate Resumes with Weightage Response:", response);
  return response.data;
}

// Enhanced resume rating with custom weightage - SYNCHRONOUS VERSION
async rateResumesSynchronously(data) {
  const response = await this.api.post('/admin/resumes/rate-batch-sync', data);
  console.log("Rate Resumes Synchronously Response:", response);
  return response.data;
}

// Get rating job status
async getRatingJobStatus(jobId) {
  const response = await this.api.get(`/admin/rating-job/${jobId}/status`);
  return response;
}

// Get rating job results
async getRatingJobResults(jobId) {
  const response = await this.api.get(`/admin/rating-job/${jobId}/results`);
  return response;
}

// Get all rating jobs
async getRatingJobs(page = 1, per_page = 20) {
  const response = await this.api.get('/admin/rating-jobs', { params: { page, per_page } });
  return response;
}

// Cancel a rating job
async cancelRatingJob(jobId) {
  const response = await this.api.delete(`/admin/rating-job/${jobId}/cancel`);
  return response.data;
}

// Retry a failed rating job synchronously
async retryRatingJobSync(jobId) {
  const response = await this.api.post(`/admin/rating-job/${jobId}/retry-sync`);
  return response.data;
}

// Get resume details with full content
async getResumeDetails(resumeId) {
  const response = await this.api.get(`/admin/resume/${resumeId}/details`);
  return response.data;
}

// Bulk upload resumes from folder
async uploadResumeFolder(formData) {
  const response = await this.api.post('/admin/resumes/upload-folder', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
}

// Export resumes data
async exportResumesData(jobDescriptionId, format = 'csv') {
  const response = await this.api.get(`/admin/resumes/export/${jobDescriptionId}`, {
    params: { format },
    responseType: 'blob'
  });
  return response;
}

// Get resume rating analytics
async getResumeAnalytics(jobDescriptionId) {
  const response = await this.api.get(`/admin/resumes/analytics/${jobDescriptionId}`);
  return response.data;
}

// ===================Shortlisted Candidate Management===================
// Send emails to shortlisted candidates
async sendShortlistEmails(jobDescriptionId, interviewId = null, expiryHours = 24) {
  const payload = {};
  if (interviewId) {
    payload.interview_id = interviewId;
    payload.expiry_hours = expiryHours;
  }
  const response = await this.api.post(`/resume/shortlisted/${jobDescriptionId}/send-emails`, payload);
  console.log("Send Shortlist Emails Response:", response);
  return response.data;
}

// Resend email to specific candidate
async resendEmail(resumeId, interviewId = null, expiryHours = 24) {
  const payload = {};
  if (interviewId) {
    payload.interview_id = interviewId;
    payload.expiry_hours = expiryHours;
  }
  const response = await this.api.post(`/resume/shortlisted/${resumeId}/resend-email`, payload);
  console.log("Resend Email Response:", response);
  return response.data;
}

// Resend emails to all shortlisted candidates
async resendAllEmails(jobDescriptionId, interviewId = null, expiryHours = 24) {
  const payload = {};
  if (interviewId) {
    payload.interview_id = interviewId;
    payload.expiry_hours = expiryHours;
  }
  const response = await this.api.post(`/resume/shortlisted/${jobDescriptionId}/resend-all`, payload);
  console.log("Resend All Emails Response:", response);
  return response.data;
}

// Link shortlisted candidates with users who have logged in
async linkShortlistedCandidates(jobDescriptionId) {
  const response = await this.api.post(`/resume/shortlisted/${jobDescriptionId}/link-candidates`);
  console.log("Link Candidates Response:", response);
  return response.data;
}

// Schedule interview for shortlisted candidates
async scheduleInterviewForShortlisted(jobDescriptionId, data) {
  const response = await this.api.post(`/resume/shortlisted/${jobDescriptionId}/schedule-interview`, data);
  console.log("Schedule Interview for Shortlisted Response:", response);
  return response.data;
}

// Get shortlisted candidates with enhanced details
async getShortlistedCandidates(jobDescriptionId) {
  const response = await this.api.get(`/resume/candidates/shortlisted?job_description_id=${jobDescriptionId}`);
  console.log("Get Shortlisted Candidates Response:", response);
  return response.data;
}

  // ===================Candidate endpoints===================
  async getCandidateDashboard() {
    const response = await this.api.get('/candidate/dashboard');
    return response.data;
  }

  async getCandidateInterviews() {
    const response = await this.api.get('/candidate/interviews');
    return response.data;
  }

  async startInterview(interviewId) {
    const response = await this.api.post(`/candidate/interview/${interviewId}/start`);
    return response.data;
  }

  async submitAnswer(interviewId, data) {
    const response = await this.api.post(`/candidate/interview/${interviewId}/answer`, data);
    return response.data;
  }

  async submitAnswerWithSpeech(interviewId, formData) {
    const response = await this.api.post(`/candidate/interview/${interviewId}/answer-with-speech`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  async completeInterview(interviewId) {
    const response = await this.api.post(`/candidate/interview/${interviewId}/complete`);
    return response.data;
  }

  async getInterviewStatus(interviewId) {
    const response = await this.api.get(`/candidate/interview/${interviewId}/status`);
    return response.data;
  }

  async getCandidateNotifications(page = 1, per_page = 20, unread_only = false) {
    const response = await this.api.get('/candidate/notifications', {
      params: { page, per_page, unread_only }
    });
    return response.data;
  }

  async markNotificationRead(notificationId) {
    const response = await this.api.put(`/candidate/notification/${notificationId}/read`);
    return response.data;
  }

  async uploadResume(formData) {
    const response = await this.api.post('/candidate/resume/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  // Speech endpoints
  async transcribeAudio(audioBlob) {
    const formData = new FormData();
    formData.append('audio', audioBlob);
    const response = await this.api.post('/speech/transcribe', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  async getSpeechAssessment(answerId) {
    const response = await this.api.get(`/speech/assessment/${answerId}`);
    return response.data;
  }

  // ===================Interview management endpoints for admin===================
  async updateInterview(interviewId, data) {
    const response = await this.api.put(`/interview/${interviewId}`, data);
    return response.data;
  }

  async deleteInterview(interviewId) {
    const response = await this.api.delete(`/interview/${interviewId}`);
    return response.data;
  }

  async addCustomQuestion(interviewId, questionData) {
    const response = await this.api.post(`/interview/${interviewId}/questions/custom`, questionData);
    return response.data;
  }

  async updateQuestion(questionId, data) {
    const response = await this.api.put(`/interview/question/${questionId}`, data);
    return response.data;
  }

  async deleteQuestion(questionId) {
    const response = await this.api.delete(`/interview/question/${questionId}`);
    return response.data;
  }
  
  async reorderQuestions(interviewId, questionOrder) {
    const response = await this.api.put(`/admin/interview/${interviewId}/questions/reorder`, {
      question_order: questionOrder
    });
    return response.data;
  }

  async generateAIQuestions(interviewId, count = 5, questionType = 'technical') {
    const response = await this.api.post(`/interview/${interviewId}/questions/generate`, { 
      question_count: count,
      question_type: questionType 
    });
    return response.data;
  }

  async getInterviewQuestions(interviewId) {
    const response = await this.api.get(`/interview/${interviewId}/questions`);
    return response.data;
  }

  // Get interview sessions (candidates who were scheduled)
  async getInterviewSessions(interviewId) {
    const response = await this.api.get(`/admin/interview/${interviewId}/sessions`);
    return response.data;
  }

  // Reschedule interview
  async rescheduleInterview(interviewId, data) {
    const response = await this.api.put(`/admin/interview/${interviewId}/reschedule`, data);
    return response.data;
  }

  // Get all candidate responses for an interview
  async getInterviewResponses(interviewId) {
    const response = await this.api.get(`/admin/interview/${interviewId}/responses`);
    return response.data;
  }

  // Generate AI questions from job description
  async generateQuestionsFromJobDescription(jobDescriptionText, questionCount = 5) {
    const response = await this.api.post('/ai/generate-questions', {
      job_description_text: jobDescriptionText,
      question_count: questionCount,
      question_type: 'technical',
      ai_provider: 'firework',
      ai_model: 'deepseek-v3'
    });
    return response.data;
  }
  // ===================NEW SECURE CANDIDATE INTERVIEW ENDPOINTS===================
  // Fixed: All methods now consistently return response.data

// Get basic interview info (before starting) - No questions/answers exposed
async getCandidateInterviewInfo(interviewId) {
  const response = await this.api.get(`/candidate/interview/${interviewId}/info`);
  return response.data; // ✅ Returns data
}

// Get single question by index (only during active interview)
async getCandidateQuestion(interviewId, questionIndex) {
  const response = await this.api.get(`/candidate/interview/${interviewId}/question/${questionIndex}`);
  console.log("Question Response:", response.data);
  return response.data; // ✅ FIXED: Now returns data instead of response
}

// Get interview progress without exposing answers
async getCandidateInterviewProgress(interviewId) {
  const response = await this.api.get(`/candidate/interview/${interviewId}/progress`);
  console.log("Progress Response:", response.data);
  return response.data; // ✅ Returns data
}

// Submit answer for current question (secure endpoint)
async submitCandidateAnswer(interviewId, data) {
  // Handle both JSON and FormData
  if (data instanceof FormData) {
    const response = await this.api.post(`/candidate/interview/${interviewId}/submit-answer`, data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data; // ✅ FIXED: Now returns data instead of response
  } else {
    const response = await this.api.post(`/candidate/interview/${interviewId}/submit-answer`, data);
    return response.data; // ✅ FIXED: Now returns data instead of response
  }
}

// Submit answer with speech/audio
async submitCandidateAnswerWithSpeech(interviewId, formData) {
  const response = await this.api.post(`/candidate/interview/${interviewId}/answer-with-speech`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
}

// ===================Mock Interview endpoints Add here===================
// Generate MCQs
async generateMCQs(formData) {
  const response = await this.api.post('/mock-interview/generate-mcq', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response;
}

// Generate Practice Questions
async generatePracticeQuestions(formData) {
  const response = await this.api.post('/mock-interview/generate-interview-questions', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  console.log("Generated Practice Questions Response:", response);
  return response;
}

// Check Answer
async checkAnswer(formData) {
  const response = await this.api.post('/mock-interview/check-answer', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response;
}

// Assess Speech Quality
async assessSpeechQuality(formData) {
  const response = await this.api.post('/mock-interview/assess-speech-quality', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response;
}

async transcribeAudioMock(audioBlob) {
  const formData = new FormData();
  formData.append('audio', audioBlob, 'recording.wav');
  
  // Change this line:
  const response = await this.api.post('/mock-interview/transcribe-audio', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response;
}

// ===================Interview Links (Direct Interview) endpoints===================
// Generate unique interview link for shortlisted candidate
async generateInterviewLink(resumeId, interviewId, expiresInHours = 24) {
  const response = await this.api.post('/interview-links/generate', {
    resume_id: resumeId,
    interview_id: interviewId,
    expires_in_hours: expiresInHours
  });
  return response.data;
}

// Get all interview links for a resume
async getResumeInterviewLinks(resumeId) {
  const response = await this.api.get(`/interview-links/resume/${resumeId}`);
  return response.data;
}

// Reopen a used/expired interview link
async reopenInterviewLink(linkId, expiresInHours = 24) {
  const response = await this.api.put(`/interview-links/${linkId}/reopen`, {
    expires_in_hours: expiresInHours
  });
  return response.data;
}

// Revoke an interview link (soft delete - marks as revoked)
async revokeInterviewLink(linkId) {
  const response = await this.api.put(`/interview-links/${linkId}/revoke`);
  return response.data;
}

// Delete an interview link permanently
async deleteInterviewLink(linkId) {
  const response = await this.api.delete(`/interview-links/${linkId}`);
  return response.data;
}

// Get all interview links for a job
async getJobInterviewLinks(jobDescriptionId, page = 1, perPage = 20, status = null) {
  const params = { page, per_page: perPage };
  if (status) params.status = status;
  
  const response = await this.api.get(`/interview-links/job/${jobDescriptionId}`, { params });
  return response.data;
}

// ===================PUBLIC Interview endpoints (no auth required)===================
// Validate interview link (public)
async validateInterviewLink(token) {
  // Use fetch directly to avoid auth headers
  const response = await fetch(`${this.api.defaults.baseURL}/interview-links/validate/${token}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to validate link');
  }
  
  const data = await response.json();
  return data.data;
}

// Get direct interview info (public)
async getDirectInterviewInfo(token) {
  const response = await fetch(`${this.api.defaults.baseURL}/interview/direct/${token}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to get interview info');
  }
  
  const data = await response.json();
  return data.data;
}

// Start direct interview (public)
async startDirectInterview(token) {
  const response = await fetch(`${this.api.defaults.baseURL}/interview/direct/${token}/start`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to start interview');
  }
  
  const data = await response.json();
  return data.data;
}

// Get direct question (public)
async getDirectQuestion(sessionId, questionIndex) {
  const response = await fetch(`${this.api.defaults.baseURL}/interview/session/${sessionId}/question/${questionIndex}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to get question');
  }
  
  const data = await response.json();
  return data.data;
}

// Submit direct answer (public)
async submitDirectAnswer(sessionId, questionId, answerText) {
  const response = await fetch(`${this.api.defaults.baseURL}/interview/session/${sessionId}/answer`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      question_id: questionId,
      answer_text: answerText
    }),
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to submit answer');
  }
  
  const data = await response.json();
  return data.data;
}

// Submit direct answer with speech (public)
async submitDirectAnswerWithSpeech(sessionId, formData) {
  const response = await fetch(`${this.api.defaults.baseURL}/interview/session/${sessionId}/answer-with-speech`, {
    method: 'POST',
    body: formData,
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to submit answer');
  }
  
  const data = await response.json();
  return data.data;
}

// Get direct progress (public)
async getDirectProgress(sessionId) {
  const response = await fetch(`${this.api.defaults.baseURL}/interview/session/${sessionId}/progress`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to get progress');
  }
  
  const data = await response.json();
  return data.data;
}

// Complete direct interview (public)
async completeDirectInterview(sessionId) {
  const response = await fetch(`${this.api.defaults.baseURL}/interview/session/${sessionId}/complete`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to complete interview');
  }
  
  const data = await response.json();
  return data.data;
}

// Bulk generate interview links for all shortlisted candidates
async bulkGenerateInterviewLinks(jobDescriptionId, interviewId, expiresInHours = 24) {
  const response = await this.api.post('/interview-links/bulk-generate', {
    job_description_id: jobDescriptionId,
    interview_id: interviewId,
    expires_in_hours: expiresInHours
  });
  return response.data;
}

}

export const apiService = new ApiService();
